import {
  CONTACT_EVENT_STATE_MAP,
  CONTACT_STATE_MAP,
  STATE_CHAT,
  STATE_WEBCHATMESSAGE,
  CHANNEL_TYPES,
  LOG_PREFIX,
} from "../../constants/constants";
import { ChatMessage } from "./../models/chat";
import { Chat } from "./../models/chat";
import { formatAttributes } from "../../utils";
import { changeAgentStateToAvailable } from "./agent";
import { disconnectChatContact, clearCurrentChatContact, getChatHistory } from "../api/chat";
import Emitter from "../../emitter";
import { API_CHAT_HISTORY, API_MILO_CHAT_CHANNEL_TRANSACTION, STATE_MISSED_CHAT } from "../constants";
import { API_CONTACT_EVENT_LOGGING_CHAT } from "../../constants/events";
import storeRTK from "../../store/storeRTK";
import Player from "../../utils/audio/player";

const lp = LOG_PREFIX + "[handlers.chat.ts]: ";
const defaultValue = {
  id: null,
  self: {},
  customer: {},
};

export const chatPlayer = new Player({ type: "CHAT", loop: true });
export const chatMsgRing = new Player({ type: "MESSAGE", loop: false });

var chats: { [key: string]: Chat } = {};

function setChat(chatObj: Chat) {
  //Below ignore duplicate chat ended events
  const existingChat = chats.hasOwnProperty(chatObj?.id);
  if (chatObj.self?.state === CONTACT_STATE_MAP.ENDED && !existingChat) return;
  if (chatObj.self?.state === CONTACT_STATE_MAP.ENDED) {
    if (chats.hasOwnProperty(chatObj?.id)) {
      delete chats[chatObj.id];
    }
  } else {
    chats[chatObj?.id] = JSON.parse(JSON.stringify(chatObj));
  }
}

export function registerChatEvent(contact: any) {
  try {
    const cnn = contact
      .getConnections()
      .find((cnn: any) => cnn.getType() === connect.ConnectionType.AGENT);
    cnn.getMediaController().then((session: any) => {
      session.onMessage(handleChatMessage);
      session.onTyping(handleChatTyping);
    });
  } catch (error) {
    console.log(lp + "Error in add chat session", error);
  }
}

export function clearChatEvents(contact: any) {
  try {
    const cnn = contact
      .getConnections()
      .find((cnn: any) => cnn.getType() === connect.ConnectionType.AGENT);

    if (!cnn) {
      console.log(lp + "No agent connection found for contact");
      return;
    }

    // Check if connection is still active
    if (cnn.getStatus() === connect.ConnectionStateType.DISCONNECTED) {
      console.log(lp + "Connection already disconnected, skipping cleanup");
      return;
    }

    cnn
      .getMediaController()
      .then((session: any) => {
        if (session) {
          session.cleanUpOnParticipantDisconnect();
        }
      })
      .catch((error: any) => {
        console.log(lp + "Error accessing media controller:", error);
      });
  } catch (error) {
    console.log(lp + "Error in clearChatEvents:", error);
  }
}

export async function getChatSession(contact: any) {
  try {
    const cnn = contact
      .getConnections()
      .find((cnn: any) => cnn.getType() === connect.ConnectionType.AGENT);
    cnn.getMediaController().then((session: any) => {
      return session;
    });
  } catch (error) {
    return null;
  }
}

function handleChatMessage(event: any) {
  const { chatDetails, data } = event;
  const message = data;
  if (
    data.ContentType === "text/plain" ||
    data.ContentType === "text/markdown"
  ) {
    const transformedMessage: ChatMessage = {
      id: message.Id,
      senderId: message.ParticipantId,
      role: message.ParticipantRole,
      text: message.Content,
      bodyType: message.ContentType,
      timestamp: message.AbsoluteTime,
      webChatConversation: {
        id: chatDetails.contactId,
      },
    };
 
    Emitter.emit(STATE_WEBCHATMESSAGE, {
      body: transformedMessage,
    });
    return transformedMessage;
  } else if (data?.Type === "ATTACHMENT") {
    const transformedMessage: ChatMessage = {
      id: message.Id,
      senderId: message.ParticipantId,
      role: message.ParticipantRole,
      text: message?.Attachments,
      bodyType: message.Type,
      timestamp: message.AbsoluteTime,
      webChatConversation: {
        id: chatDetails.contactId ? chatDetails.contactId : message?.ContactId,
      },
    };
    Emitter.emit(STATE_WEBCHATMESSAGE, {
      body: transformedMessage,
    });
    return transformedMessage;
  }
}

function handleChatTyping(event: any) {
  const { chatDetails, data } = event;
  const message = data;
  if (data?.ParticipantRole === "CUSTOMER") {
    if (
      data.ContentType === "text/plain" ||
      data.ContentType === "text/markdown" ||
      data.ContentType === "application/vnd.amazonaws.connect.event.typing"
    ) {
      const transformedMessage: ChatMessage = {
        id: message?.Id,
        senderId: message?.ParticipantId,
        role: message?.ParticipantRole,
        text: message?.Content,
        bodyType: message?.ContentType,
        timestamp: message?.AbsoluteTime,
        webChatConversation: {
          id: chatDetails?.contactId,
        },
      };
      Emitter.emit(STATE_WEBCHATMESSAGE, {
        body: transformedMessage,
      });
      return transformedMessage;
    }
  }
}

export function handleIncomingChat(contact: any) {
  console.log(
    lp + "INCOMING/RINGING chat contact event: ",
    contact,
    contact.getAttributes()
  );
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.INCOMING, contact);
  return {
    contactId: contact.contactId || contact.getContactId(),
    eventType: CONTACT_EVENT_STATE_MAP.RINGING,
    channel: CHANNEL_TYPES.CHAT,
  };
}

export function handleConnectingChat(contact: any) {
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.INCOMING, contact);
}

export function handleAcceptedChat(contact: any) {
  console.log(
    lp +
      "ACCEPTED chat contact event:. Contact state is " +
      contact.getStatus(),
    contact
  );
  Emitter.emit(API_CONTACT_EVENT_LOGGING_CHAT, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.ESTABLISHED,
      channel: CHANNEL_TYPES.CHAT,
    },
  });
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.ACCEPTED, contact);
}

export function handleConnectedChat(contact: any) {
  console.log(
    lp +
      "CONNECTED chat contact event:. Contact state is " +
      contact.getStatus(),
    contact
  );
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.CONNECTED, contact);
  getChatHistory({
    type: 'req on contact accepted',
    chan: API_CHAT_HISTORY,
    body: {
      conversationId: contact.contactId || contact.getContactId(),
      nextHistoryToken: null,
    },
  }, true);
    registerChatEvent(contact);
  Emitter.emit(API_CONTACT_EVENT_LOGGING_CHAT, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.ESTABLISHED,
      channel: CHANNEL_TYPES.CHAT,
    },
  });
  // Send message to Notification worker to open milo URL
  Emitter.emit(API_MILO_CHAT_CHANNEL_TRANSACTION, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
    },
  });
}

export function handleRefreshChat(contact: any) {
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.REFRESH, contact);
}

export function handleMissedChat(contact: any) {
  console.log(
    lp + "MISSED chat contact customer state: ",
    contact.getActiveInitialConnection(),
    contact
  );

  try {
    if (contact.getActiveInitialConnection() !== null) {
      // changeAgentStateToAvailable();
    } else {
      console.log(
        `${lp} Contact ${contact.getContactId()} was missed but not changing agent status due to initial connection(customer) disconnected.`
      );
    }

    // Get customer information for the missed chat notification
    const customerEndpoint = contact.getActiveInitialConnection()?.getEndpoint();
    const customerAttributes = formatAttributes(contact.getAttributes());
    const queueInfo = contact.getQueue();
    const agentConnection = contact.getAgentConnection();
    try {
    Emitter.emit(API_CONTACT_EVENT_LOGGING_CHAT, {
      body: {
        contactId: contact.contactId || contact.getContactId(),
        eventType: CONTACT_EVENT_STATE_MAP.MISSED,
        channel: CHANNEL_TYPES.CHAT,
      },
    });

    // Emit enhanced missed chat event for notifications
    Emitter.emit(STATE_MISSED_CHAT, {
      chan: STATE_MISSED_CHAT,
      body: {
        contactId: contact.contactId || contact.getContactId(),
        eventType: CONTACT_EVENT_STATE_MAP.MISSED,
        channel: CHANNEL_TYPES.CHAT,
        // Customer information
        customerName: customerAttributes?.FIRSTNAME + ' ' + customerAttributes?.LASTNAME || 
                    customerEndpoint?.name || 
                    'Unknown Customer',
        phoneNumber: customerEndpoint?.phoneNumber || 
                    customerAttributes?.Customer_ANI || 
                    customerAttributes?.Customer_Phone || 
                    'Unknown Number',
        // Queue information
        queueName: queueInfo?.name || 'Unknown Queue',
        queueId: queueInfo?.queueARN || null,
        // Contact details
        direction: contact.isInbound() ? 'INBOUND' : 'OUTBOUND',
        contactStartTime: agentConnection.getState().timestamp || new Date().toISOString(),
        missedAt: new Date().toISOString(),
        // Additional attributes that might be useful
        customerAttributes: customerAttributes,
        // Agent context
        agentId: agentConnection?.connectionId || null,
        // Contact duration (if available)
        contactDuration: (new Date().getTime() - new Date(agentConnection.getState().timestamp).getTime()) || 0,
        // Chat specific information
        chatSessionId: contact.getContactId(),
      },
    });
    } catch (error) {
      console.log(lp + "Error emitting STATE_MISSED_CHAT event", error);
    }
    // Wrap clearChatEvents in a try-catch to handle any errors
    try {
      clearChatEvents(contact);
    } catch (error) {
      console.log(lp + "Error clearing chat events:", error);
    }

    updateChatAndSend(CONTACT_EVENT_STATE_MAP.MISSED, contact);
  } catch (error) {
    console.log(lp + "Error in handleMissedChat:", error);
    // Still try to update the chat state even if there's an error
    updateChatAndSend(CONTACT_EVENT_STATE_MAP.MISSED, contact);
  }
}

export function handleDestroyChat(contact: any) {
  Emitter.emit(API_CONTACT_EVENT_LOGGING_CHAT, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.RELEASED,
      channel: CHANNEL_TYPES.CHAT,
    },
  });
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.DESTROYED, contact);
}

export function handleEndedChat(contact: any) {
  if (contact.getStatus().type == 'error') {
  }
  Emitter.emit(API_CONTACT_EVENT_LOGGING_CHAT, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.RELEASED,
      channel: CHANNEL_TYPES.CHAT,
    },
  });
  updateChatAndSend(CONTACT_EVENT_STATE_MAP.ENDED, contact);
}

export function updateChatAndSend(type: any, contact: any) {
  let chat = updateChat(type, contact);
  const existingChat = Object.assign(
    {},
    chats[contact.contactId || contact.getContactId()]
  );
  if (JSON.stringify(existingChat) === JSON.stringify(chat)) return;
  if (chat) {
    Emitter.emit(STATE_CHAT, { body: { ...chat }, success: true });
    setChat({ ...chat });
  }
  chat = null;
}

function updateChat(contactEventType: string, contact: any): Chat | null {
  try {
    let id = contact.contactId || contact.getContactId();
    let chat: any = chats[id];
    switch (contactEventType) {
      case CONTACT_EVENT_STATE_MAP.INITIAL:
        let state = contact.getState();
        try {
          const cstate = contact.getState();
          const ctype: string = cstate?.type;
          let cstate_type: string = (CONTACT_EVENT_STATE_MAP as any)[
            ctype.toUpperCase()
          ];
          if (
            cstate_type === CONTACT_EVENT_STATE_MAP.ENDED ||
            ctype === "error"
          ) {
            return chat;
          }
        } catch (error) {
          console.log(lp + "Error during state check of chat", contact, error);
        }
        // This handling handles initial scenerio when chat event arrieves first time
        if (!chat) {
          // Get chats from Redux store
          const chats = storeRTK.getState().chat.chats;
          const contactId = contact.contactId || contact.getContactId();
          const chatExists = chats.some((c: any) => c.id === contactId);
          if (chatExists) {
            chat = chats.find((c: any) => c.id === contactId);
          } else {
            chat = defaultValue;
          }
        }

        return {
          ...chat,
          id: contact.contactId || contact.getContactId(),
          selfState: CONTACT_STATE_MAP.INCOMING,
          self: {
            ...chat.self,
            type: "AGENT",
            state: CONTACT_STATE_MAP.INCOMING,
            id: contact.getAgentConnection().getConnectionId(),
            notes: "",
            endTime: "",
            connectedTime: chat.self.connectedTime || undefined,
          },
          customer: {
            ...chat.customer,
            state: CONTACT_STATE_MAP.CONNECTED,
            type: "CUSTOMER",
            connectedTime: state.timestamp,
            attributes: formatAttributes(contact.getAttributes()),
            name: contact.getAttributes()?.customerName?.value || "",
          },
        };

      case CONTACT_EVENT_STATE_MAP.INCOMING:
        return chat;

      case CONTACT_EVENT_STATE_MAP.ACCEPTED:
      case CONTACT_EVENT_STATE_MAP.CONNECTED:
        return {
          ...chat,
          selfState: CONTACT_STATE_MAP.CONNECTED,
          self: {
            ...chat.self,
            state: CONTACT_STATE_MAP.CONNECTED,
            connectedTime: chat.self.connectedTime || new Date().getTime(),
          },
          customer: {
            ...chat.customer,
            name:
              chat.customer.name ||
              contact.getAttributes()?.customerName?.value ||
              "",
            attributes: formatAttributes(contact.getAttributes()),
          },
        };
      case CONTACT_EVENT_STATE_MAP.REFRESH:
        const rstate = contact.getState();
        const rcontactId = contact.contactId || contact.getContactId();
        const type: string = rstate.type;
        if (type === "error") {
          if ((chat && chat.id === rcontactId) || !chat?.id) {
            disconnectChatContact(rcontactId);
            return chat;
          } else {
            return chat;
          }
        }

        let selfState: string = (CONTACT_STATE_MAP as any)[type.toUpperCase()];
        if (
          chat &&
          chat.id === (contact.contactId || contact.getContactId()) &&
          selfState !== CONTACT_EVENT_STATE_MAP.ENDED
        ) {
          chat.customer.attributes = formatAttributes(contact.getAttributes());
        }
        return chat;

      case CONTACT_EVENT_STATE_MAP.MISSED:
        const contactId = contact.contactId || contact.getContactId();
        clearCurrentChatContact(contactId);
        return {
          ...chat,
          self: {
            ...chat.self,
            missed: true,
          },
        };
      case CONTACT_EVENT_STATE_MAP.DESTROYED:
      case CONTACT_EVENT_STATE_MAP.ENDED:
        if (!chat) {
          chat = defaultValue;
          return {
            ...chat,
            selfState: CONTACT_STATE_MAP.ENDED,
            id: contact.contactId || contact.getContactId(),
            self: {
              ...chat.self,
              state: CONTACT_STATE_MAP.ENDED,
            },
          };
        }
        if (chat.id === contact.contactId || chat.id === contact.getContactId()) {
          const newChat = {
            ...chat,
            selfState: CONTACT_STATE_MAP.ENDED,
            self: {
              ...chat.self,
              state: CONTACT_STATE_MAP.ENDED,
              endTime: chat.self?.endTime || new Date().getTime(),
            },
          };
          return newChat;
        }
        return chat;
      default:
        return chat;
    }
  } catch (err) {
    console.log(lp + "Error in updating chat" + contact, err);
    return null;
  }
}
